import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { FormikInput, Button } from '../../../../../components/UI';
import Checkbox from '../../../../../components/UI/Checkbox';
import { NewIntegrationFormSchema, NewIntegrationFormSchemaWithAdobe } from '../../../../../schemas';
import { useUserContext } from '../../../../../contexts/UserContext';
import { addNewAdobeIoProduct, validateTokenAndWorkspace } from '../../../../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../../../../constants/analyticsEvents';
import Loader from '../../../../../components/UI/Loader/Loader';
import { useNavigate } from 'react-router-dom';
import backwardArrow from '../../../../../assets/icons/backward-arrow.svg';
import { permission } from '../../../../../config/permission';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../../../../contexts/ContentfulContext';
import { Eye, EyeOff } from 'lucide-react';

const NewIntegration = () => {
  const { userData, checkPermission, updateAdobeIoIntegrations, updateDefaultAdobeIO, appData } = useUserContext();
  const { content } = useContentfulContext();

  const navigate = useNavigate();
  const [scopes, setScopes] = useState([]);
  const [selectedScope, setSelectedScope] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  // const [organization, setOrganization] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    client_key: false,
    x_api_key: false
  });
  const [, setIsValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidationComplete, setIsValidationComplete] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [validationSuccess, setValidationSuccess] = useState("");

  const disableFieldWithPermission = !checkPermission(permission.EDIT_ADOBE_IO);

  const handleActualSubmit = async (values) => {
    if (!isValidationComplete) {
      toast.error("Please validate the integration first before submitting.");
      return;
    }
    
    try {
      setLoading(true);

      const data = {
        integration_name: values.integration_name,
        client_key: values.client_key,
        x_API_Key: values.x_api_key,
        organization: values.organization_name,
        scope: selectedScope,
        customer_id: values.customer_id,
        technical_account_id: values.technical_account_id,
        technical_account_email: values.technical_account_email,
        products: selectedProducts.map((product) => ({
          name: product,
        })),
      };

      if(selectedProducts.includes("adobe_target")) {
        data.client_name = values.client_name
        data.tenant_name = values.tenant_name
      }

      const result = await addNewAdobeIoProduct(userData.token, {
        organizationId: userData.selectedOrganization.org_token,
        data,
      }, {
        event_name: ANALYTICS_EVENTS.CREATE_ADOBE_IO_INTEGRATION,
        metadata: { integration_name: data.integration_name, products: selectedProducts }
      });
      !loading && toast.success(content.NEW_INTEGRATION_CONTENT.success);
      updateAdobeIoIntegrations()
      if(!userData?.defaultAdobeIO) {
        updateDefaultAdobeIO(result.data)
      }
      setTimeout(() => {
        navigate('/setup/adobeIO-integrations');
      }, 1000);
    } catch (error) {
      toast.error(error?.message || "default error");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (e, setFieldValue) => {
    const value = e.target.value;
    if (e.target.checked) {
      const updatedValue = [...selectedProducts, value];
      setSelectedProducts(updatedValue);
      setFieldValue("products", updatedValue)
      
      const mergedScope = getMergedScopes(updatedValue)
      setSelectedScope(mergedScope);
      setFieldValue("scope", mergedScope)
    } else {
      const updatedValue = selectedProducts.filter(item => item !== value)
      const mergedScope = getMergedScopes(updatedValue)

      setSelectedScope(mergedScope);
      setFieldValue("scope", mergedScope)

      setSelectedProducts(updatedValue);
      setFieldValue("products", updatedValue)
  }
  
  // Reset validation state when products change
  resetValidationState();
};

const handleChangeScope = (e, setFieldValue) => {
  setSelectedScope(e.target.value)
  setFieldValue("scope", e.target.value)
  
  // Reset validation state when scope changes
  resetValidationState();
}

// Add this helper function
const resetValidationState = () => {
  setIsValidationComplete(false);
  setIsValidated(false);
  setValidationSuccess("");
  setValidationError("");
};

  const getMergedScopes = (arr) => {
    return scopes
      .filter(item => arr.includes(item.id))
      .map(item => item.scope.split(','))
      .flat()
      .filter((value, index, self) => value && self.indexOf(value) === index)
      .join(',');
  }


  useEffect(() => {
    if(appData?.scopes) 
      setScopes(appData?.scopes)
  }, [appData?.scopes])

  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleValidate = async (values, { setSubmitting, setTouched, validateForm, setFieldValue }) => {
    try {
      setIsValidating(true);
      setValidationError(""); // Clear previous errors
      
      // Trigger all field validations by setting all fields as touched
      const allFields = Object.keys(values);
      const touchedFields = allFields.reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {});
      setTouched(touchedFields);
      
      // Validate the form
      const errors = await validateForm(values);
      
      // If there are validation errors, stop here
      if (Object.keys(errors).length > 0) {
        toast.error("Please fix the form errors before validating.");
        setSubmitting(false);
        return;
      }
  
      // Prepare the Adobe IO data from form
      const adobeIOData = {
        integration_name: values.integration_name,
        client_key: values.client_key,
        x_API_Key: values.x_api_key,
        organization: values.organization_name,
        scope: selectedScope,
        technical_account_id: values.technical_account_id,
        technical_account_email: values.technical_account_email,
        products: selectedProducts.map((product) => ({
          name: product,
        })),
      };
  
      if(selectedProducts.includes("adobe_target")) {
        adobeIOData.client_name = values.client_name;
        adobeIOData.tenant_name = values.tenant_name;
      }
  
      const result = await validateTokenAndWorkspace(
        userData.token,
        userData.selectedOrganization.org_token,
        adobeIOData.tenant_name || values.tenant_name,
        adobeIOData
      );
  
      if (result?.status) {
        setIsValidated(true);
        setIsValidationComplete(true);
        setValidationSuccess("Integration validated successfully. You can now submit the form.");
        setValidationError("");
        // Set customer_id from discovery API response
        if (result?.data?.customer_id) {
          setFieldValue("customer_id", result.data.customer_id);
        }
      } else {
        setIsValidated(false);
        setIsValidationComplete(false);
        setValidationSuccess("");
        setValidationError("Invalid Adobe IO integration. Please check your inputs.");
      }
    } catch (error) {
      const errorMsg = error?.message || content?.COMMON_MESSAGE.something_wrong_msg;
      setIsValidated(false);
      setIsValidationComplete(false);
      setValidationSuccess("");
      
      if (errorMsg.includes("Token generation failed")) {
        setValidationError("Invalid Adobe IO integration: Token generation failed.");
      } else {
        setValidationError(errorMsg);
      }
    } finally {
      setIsValidating(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-4 md:px-8 my-6">
      {loading && <Loader />}
      <div className="flex flex-col lg:w-1/2 justify-center items-start">
        <div className="space-y-4 md:space-y-6 w-full">
          <a href="#" className="font-medium text-primary hover:underline flex items-center" onClick={(e) => {
            e.preventDefault();
            navigate('/setup/adobeIO-integrations')
          }}>
            <img src={backwardArrow || "/placeholder.svg"} alt='' className='w-5 h-5 mr-2' /> {content.NEW_INTEGRATION_CONTENT.back_cta_text} 
          </a>
          <Formik
            initialValues={{
              products: [],
              scope: "",
              integration_name: "",
              client_key: "",
              x_api_key: "",
              client_name: "",
              tenant_name: "",
              customer_id: "",
              organization_name: "",
              technical_account_id: "",
              technical_account_email: "",
            }}
            validationSchema={() => selectedProducts?.includes('adobe_target') ? NewIntegrationFormSchemaWithAdobe(content.NEW_INTEGRATION_CONTENT) : NewIntegrationFormSchema(content.NEW_INTEGRATION_CONTENT)}
            onSubmit={handleActualSubmit}
          >
            {({ errors, touched, setFieldValue, values, validateForm, setTouched, setSubmitting }) => (
              <Form id="adobeIo-integration-form" action="#" className="space-y-4">
                <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">{content.NEW_INTEGRATION_CONTENT.title}</h2>
                <p>{content.NEW_INTEGRATION_CONTENT.description}</p>

                <FormikInput
                  type="text"
                  disabled={disableFieldWithPermission}
                  id="integration_name"
                  name="integration_name"
                  label={content.NEW_INTEGRATION_CONTENT.formLabels.integration_name}
                  helperText={content.NEW_INTEGRATION_CONTENT.help_text.integration_name}
                />

                <FormikInput
                  type={showPassword.client_key ? "text" : "password"}
                  disabled={disableFieldWithPermission}
                  id="client_key"
                  name="client_key"
                  label={content.NEW_INTEGRATION_CONTENT.formLabels.client_key}
                  helperText={content.NEW_INTEGRATION_CONTENT.help_text.client_key}
                  onChange={() => resetValidationState()}
                  suffixIcon={
                    <button
                      type="button"
                      onClick={() => toggleShowPassword("client_key")}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showPassword.client_key ? "Hide client key" : "Show client key"}
                    >
                      {showPassword.client_key ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                <FormikInput
                  type={showPassword.x_api_key ? "text" : "password"}
                  disabled={disableFieldWithPermission}
                  id="x_api_key"
                  name="x_api_key"
                  label={content.NEW_INTEGRATION_CONTENT.formLabels.x_api_key}
                  helperText={content.NEW_INTEGRATION_CONTENT.help_text.x_api_key}
                  onChange={() => resetValidationState()}
                  suffixIcon={
                    <button
                      type="button"
                      onClick={() => toggleShowPassword("x_api_key")}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showPassword.x_api_key ? "Hide API key" : "Show API key"}
                    >
                      {showPassword.x_api_key ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                {isValidationComplete && values.customer_id && (
                  <FormikInput
                    type="text"
                    disabled
                    id="customer_id"
                    name="customer_id"
                    label={content.NEW_INTEGRATION_CONTENT.formLabels.customer_id}
                    helperText={content.NEW_INTEGRATION_CONTENT.help_text.customer_id}
                  />
                )}

                <FormikInput
                  type="text"
                  disabled={disableFieldWithPermission}
                  id="organization_name"
                  name="organization_name"
                  label={content.NEW_INTEGRATION_CONTENT.formLabels.organization_name}
                  helperText={content.NEW_INTEGRATION_CONTENT.help_text.organization_name}
                />

                <FormikInput
                  type="text"
                  disabled={disableFieldWithPermission}
                  id="scope"
                  name="scope"
                  value={selectedScope}
                  label={content.NEW_INTEGRATION_CONTENT.formLabels.scope}
                  helperText={content.NEW_INTEGRATION_CONTENT.help_text.scope}
                  onChange={(e) => handleChangeScope(e, setFieldValue)}
                />

                <FormikInput
                  type="text"
                  disabled={disableFieldWithPermission}
                  id="technical_account_id"
                  name="technical_account_id"
                  label={content.NEW_INTEGRATION_CONTENT.formLabels.technical_account_id}
                  helperText={content.NEW_INTEGRATION_CONTENT.help_text.technical_account_id}
                />

                <FormikInput
                  type="text"
                  disabled={disableFieldWithPermission}
                  id="technical_account_email"
                  name="technical_account_email"
                  label={content.NEW_INTEGRATION_CONTENT.formLabels.technical_account_email}
                  helperText={content.NEW_INTEGRATION_CONTENT.help_text.technical_account_email}
                />

                <div className="mb-4">
                  <div className="relative">
                    <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-2">{content.NEW_INTEGRATION_CONTENT.select_product.title}</h2>
                    <p>{content.NEW_INTEGRATION_CONTENT.select_product.description}</p>
                    <ul className="flex flex-col w-full gap-4 justify-center my-4">
                      {content.NEW_INTEGRATION_CONTENT.select_product.products.map((item) => {
                          return (
                            <li key={item.key} className="flex flex-col gap-3">
                              <label htmlFor={item.key} className="w-56 p-4 inline-flex items-center justify-center text-gray-500 bg-white border-2 border-gray-200 rounded-lg cursor-pointer peer-checked:border-primary hover:text-gray-600 peer-checked:text-gray-600 hover:bg-gray-50"
                              onChange={(e) => handleCheckboxChange(e, setFieldValue)}>
                                <Checkbox elId={item.key} value={item.key} disable={disableFieldWithPermission} selected={selectedProducts.indexOf(item.key) !== -1} className="hidden" />
                                <p className="text-md font-bold leading-tight tracking-tight text-black text-center ml-2">{item.name}</p>
                              </label>

                              {item.key === "adobe_target" && selectedProducts.includes(item.key) && (
                                <div className="space-y-4">
                                  <FormikInput
                                    type="text"
                                    disabled={disableFieldWithPermission}
                                    id="client_name"
                                    name="client_name"
                                    label={content.NEW_INTEGRATION_CONTENT.formLabels.client_name}
                                    onChange={() => resetValidationState()}
                                  />
                                  <FormikInput
                                    type="text"
                                    disabled={disableFieldWithPermission}
                                    id="tenant_name"
                                    name="tenant_name"
                                    label={content.NEW_INTEGRATION_CONTENT.formLabels.tenant_name}
                                    onChange={() => resetValidationState()}
                                  />
                                </div>
                              )}
                            </li>
                          )})}
                    </ul>
                  {errors.products && touched.products ? <p className="mt-2 text-sm text-red-5">{errors.products}</p> : null}
                  </div>
                </div>

                {validationSuccess && (
                  <div className="p-2 text-green-700">
                    ✓ {validationSuccess}
                  </div>
                )}
                {validationError && (
                  <div className="p-2 text-red-700">
                    ✗ {validationError}
                  </div>
                )}

                <div className="flex justify-start items-center gap-2 pt-2">
                  {checkPermission(permission.CREATE_ADOBE_IO) && (
                    <Button 
                      onClick={() => handleValidate(values, { setSubmitting, setTouched, validateForm, setFieldValue })} 
                      type="button" 
                      variant="secondary"
                      disabled={isValidationComplete || isValidating}
                    >
                      {isValidating ? content.NEW_INTEGRATION_CONTENT.validating_cta : content.NEW_INTEGRATION_CONTENT.validate_cta}
                    </Button>
                  )}
                  {checkPermission(permission.CREATE_ADOBE_IO) && (
                    <Button 
                      type="submit" 
                      variant="primary"
                      disabled={!isValidationComplete || loading}
                      className={!isValidationComplete ? "opacity-50 cursor-not-allowed" : ""}
                    >
                      {loading ? content.NEW_INTEGRATION_CONTENT.submitting_cta : content.NEW_INTEGRATION_CONTENT.submit_cta}
                    </Button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default NewIntegration;